import { createContext, useContext, useState } from 'react';
import { useFlatsDataContext } from './FlatsDataContext';
import { paths } from '../settings';
import { TFlat } from '../shared/types/flat';

type TSVGContext = {
    currentSVG: string;
    handleContextFloorClick: (elementId: string) => void;
    handleContextFlatClick: (flat: any) => void;
    handleContextFlatGraphicClick: (flatID: string) => void;
    handleResetToGraphicInitialState: () => void;
    handleHideFlatDetailsPane: () => void;
};

const SVGContext = createContext<TSVGContext | undefined>(undefined);

export const SVGProvider: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
    const [ currentSVG, setCurrentSVG ] = useState<string>(`${paths.assets}/buildings/building_A.svg`);
    const { contextFlats, contextSelectedFloor, setContextSelectedFlat, setContextSetShowSidebar, setFilterAvailable, setContextSelectedFloor, setSecondarySingleFilter } = useFlatsDataContext();

    /**
     * Handles click event on a Floor element, updating the current SVG to the selected floor's SVG.
     * @param {string} floorName - The name of the selected Floor.
    */
    const handleContextFloorClick = (floorName: string): void => {
        setContextSelectedFloor(floorName);
        setCurrentSVG(`${paths.assets}/floors/${floorName}.svg`);
    };

    /**
     * Handles click event on a Flat element, updating the current SVG and selecting the Flat.
     * @param {TFlat} flat - The Flat object.
    */
    const handleContextFlatClick = (flat: TFlat): void => {
        setCurrentSVG(`${paths.assets}/floors/${flat.floor.slug}.svg`);
        setContextSelectedFlat(flat);
    };

    /**
     * Handles click event on a Floor SVG flat element, selecting the corresponding Flat by it's name.
     * @param {string} flatName - The name of the clicked Flat.
    */
    const handleContextFlatGraphicClick = (flatName: string): void => {
        setContextSelectedFlat(contextFlats.find(flat => flat.name.toLowerCase() === flatName.toLowerCase()))
    };

    /**
     * Hides the Flat details pane, otherwise resets to the initial SVG state if no Floor is selected.
    */
    const handleHideFlatDetailsPane = (): void => {
        if(contextSelectedFloor !== '') {
            setContextSelectedFlat({name: ''});
        }
        else {
            handleResetToGraphicInitialState();
        }
    };

    /**
     * Resets the SVG to it's initial Building state, clearing selections and filters.
     */
    const handleResetToGraphicInitialState = (): void => {
        setCurrentSVG(`${paths.assets}/buildings/building_A.svg`);
        setContextSelectedFlat({name: ''});
        setContextSelectedFloor('');
        setContextSetShowSidebar(false);
        setFilterAvailable([]);
        setSecondarySingleFilter('areaFrom', null);
        setSecondarySingleFilter('areaTo', null);
        setSecondarySingleFilter('rooms', []);
    };

    return (
        <SVGContext.Provider value={{ 
                currentSVG, 
                handleContextFloorClick, 
                handleContextFlatClick, 
                handleContextFlatGraphicClick, 
                handleResetToGraphicInitialState,
                handleHideFlatDetailsPane
            }}>
            {children}
        </SVGContext.Provider>
    );
};

export const useSVGContext = (): TSVGContext => {
    const context = useContext(SVGContext);
    if (!context) {
        throw new Error('useSVGContext must be used within an SVGProvider');
    }
    return context;
};